import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronDown, Copy, ExternalLink, Loader2, Mail, MessageCircle, Send } from 'lucide-react';
import { toast } from 'sonner';

import API_BASE from '../../utils/apiBase';
import StaffDirectLinks from './StaffDirectLinks';
import { buildDBSApplicantMessage } from '../../lib/dbsApplicantMessage';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  createMessageRequestId,
  explainWhatsAppProviderFailure,
  manualWhatsAppSupportWindow,
} from '../../lib/manualMessaging';

const API = API_BASE;

function safeDetail(error, fallback) {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' && detail.trim() ? detail : fallback;
}

export function buildManualApplicantWhatsAppHelpMessage(firstName, secureLink, nextActionTitle = '') {
  const safeName = String(firstName || '').trim();
  const greeting = safeName ? `Hi ${safeName},` : 'Hi,';
  const actionLine = String(nextActionTitle || '').trim();
  return [
    `${greeting} this is Osabea Healthcare Solutions.`,
    '',
    actionLine ? `Your next CareOps step is: ${actionLine}.` : 'Please continue your CareOps application or onboarding.',
    '',
    'Here is your secure CareOps link:',
    secureLink,
    '',
    'Please use the secure link for documents and sensitive information. If you have any trouble logging in or completing the step, reply to me on WhatsApp and I’ll help you through it.',
  ].join('\n');
}

function formatDateTime(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed);
}

export default function PersonMessageComposer({
  domain,
  employeeId,
  token,
  messages = [],
  threadId = null,
  emailEnabled = true,
  emailReason = '',
  readOnly = false,
  personFirstName = '',
  nextActionTitle = '',
  initialChannel = 'email',
  draft,
  onDraftChange,
  onSent,
}) {
  const [channel, setChannel] = useState(initialChannel === 'whatsapp' ? 'whatsapp' : 'email');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [dbsToolsOpen, setDbsToolsOpen] = useState(false);
  const [dbsOrganisationReference, setDbsOrganisationReference] = useState('');
  const [dbsOrganisationCode, setDbsOrganisationCode] = useState('');
  const [sending, setSending] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [requestId, setRequestId] = useState('');
  const [openerRequestId, setOpenerRequestId] = useState('');
  const [openerSent, setOpenerSent] = useState(false);
  const [lastWhatsAppRecipient, setLastWhatsAppRecipient] = useState('');
  const [accessRequestId, setAccessRequestId] = useState('');
  const [accessSent, setAccessSent] = useState(false);
  const [trainingBooking, setTrainingBooking] = useState({
    course_title: '', training_date: '', arrival_time: '',
    training_time: '', venue_address: '',
  });
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [trainingBookingRequestId, setTrainingBookingRequestId] = useState('');
  const [copyingManualHelp, setCopyingManualHelp] = useState(false);
  const [openingManualHelp, setOpeningManualHelp] = useState(false);
  const [manualHelpPrepared, setManualHelpPrepared] = useState(null);
  const [whatsappActionsExpanded, setWhatsAppActionsExpanded] = useState(false);
  const [chaseOptions, setChaseOptions] = useState([]);
  const [chaseOptionsLoading, setChaseOptionsLoading] = useState(false);
  const [chaseActionsExpanded, setChaseActionsExpanded] = useState(false);
  const [emailTasksExpanded, setEmailTasksExpanded] = useState(false);
  const [chaseBusyKey, setChaseBusyKey] = useState('');
  const [chaseRequestIds, setChaseRequestIds] = useState({});
  const [selectedChaseKeys, setSelectedChaseKeys] = useState([]);
  const controlledDraft = draft && typeof draft === 'object';
  const subjectValue = controlledDraft ? String(draft.subject || '') : subject;
  const bodyValue = controlledDraft ? String(draft.body || '') : body;
  const channelValue = controlledDraft
    ? (draft.channel === 'whatsapp' ? 'whatsapp' : 'email')
    : channel;

  const updateDraftField = (field, value) => {
    if (!controlledDraft) {
      if (field === 'subject') setSubject(value);
      if (field === 'body') setBody(value);
      if (field === 'channel') setChannel(value);
    }
    onDraftChange?.({
      subject: field === 'subject' ? value : subjectValue,
      body: field === 'body' ? value : bodyValue,
      channel: field === 'channel' ? value : channelValue,
    });
  };

  const clearDraft = ({ keepSubject = false } = {}) => {
    if (!controlledDraft) {
      if (!keepSubject) setSubject('');
      setBody('');
    }
    onDraftChange?.({
      subject: keepSubject ? subjectValue : '',
      body: '',
      channel: channelValue,
    });
  };

  useEffect(() => {
    setChannel(initialChannel === 'whatsapp' ? 'whatsapp' : 'email');
    setWhatsAppActionsExpanded(false);
    setChaseActionsExpanded(false);
    setEmailTasksExpanded(false);
    setChaseOptions([]);
    setChaseRequestIds({});
    setSelectedChaseKeys([]);
  }, [employeeId, initialChannel]);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );
  const supportWindow = useMemo(
    () => manualWhatsAppSupportWindow(messages),
    [messages],
  );
  const outboundWhatsAppMessages = useMemo(() => (
    (Array.isArray(messages) ? messages : [])
      .filter((message) => (
        String(message?.channel || '').toLowerCase() === 'whatsapp'
        && String(message?.direction || '').toLowerCase() === 'outbound'
      ))
      .sort((left, right) => (
        new Date(right?.created_at || 0).getTime() - new Date(left?.created_at || 0).getTime()
      ))
  ), [messages]);
  const priorConversationInvitation = useMemo(() => (
    outboundWhatsAppMessages.find((message) => (
      String(message?.template_key || '').toLowerCase() === 'conversation_start'
      && String(message?.delivery_status || '').toLowerCase() !== 'failed'
    )) || null
  ), [outboundWhatsAppMessages]);
  const latestOutboundWhatsApp = outboundWhatsAppMessages[0] || null;
  const latestFailedWhatsApp = (
    String(latestOutboundWhatsApp?.delivery_status || '').toLowerCase() === 'failed'
      ? latestOutboundWhatsApp
      : null
  );
  const openerAlreadySent = openerSent || Boolean(priorConversationInvitation);
  const knownWhatsAppRecipient = String(
    lastWhatsAppRecipient || outboundWhatsAppMessages[0]?.to_phone || '',
  ).trim();
  const latestFailure = useMemo(
    () => explainWhatsAppProviderFailure(latestFailedWhatsApp),
    [latestFailedWhatsApp],
  );
  const staffDomain = domain === 'staff';

  const notifySent = async (data) => {
    if (onSent) await onSent(data || null);
    if (!staffDomain && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('careops:recruitment-communication-updated', {
        detail: { employeeId },
      }));
    }
  };

  useEffect(() => {
    let active = true;
    if (!token) return () => { active = false; };

    const endpoint = staffDomain
      ? `${API}/staff-communications/whatsapp/status`
      : `${API}/onboarding-communications/whatsapp/status`;

    axios.get(endpoint, { headers })
      .then((response) => {
        if (active) setWhatsappStatus(response.data || null);
      })
      .catch(() => {
        if (active) setWhatsappStatus(null);
      });

    return () => { active = false; };
  }, [headers, staffDomain, token]);

  useEffect(() => {
    let active = true;
    if (!token || !employeeId || readOnly || (channelValue !== 'whatsapp' && !emailTasksExpanded)) {
      return () => { active = false; };
    }

    const endpoint = staffDomain
      ? `${API}/employees/${employeeId}/staff-communications/chase-options`
      : `${API}/employees/${employeeId}/communications/chase-options`;

    setChaseOptionsLoading(true);
    axios.get(endpoint, { headers })
      .then((response) => {
        if (!active) return;
        const nextOptions = Array.isArray(response.data?.actions) ? response.data.actions : [];
        setChaseOptions(nextOptions);
        const currentKeys = new Set(nextOptions.map((action) => String(action?.key || '')));
        setSelectedChaseKeys((current) => current.filter((key) => currentKeys.has(key)));
      })
      .catch(() => {
        if (active) setChaseOptions([]);
      })
      .finally(() => {
        if (active) setChaseOptionsLoading(false);
      });

    return () => { active = false; };
  }, [channelValue, emailTasksExpanded, employeeId, headers, readOnly, staffDomain, token]);

  const manualWhatsAppReady = whatsappStatus?.manual_support_ready === true;
  const conversationStartReady = whatsappStatus?.conversation_start_ready === true;
  const conversationStartMapped = whatsappStatus?.conversation_start_mapped === true;
  const conversationStartProvider = whatsappStatus?.conversation_start_provider || {};
  const conversationStartStatus = String(conversationStartProvider?.status || '').toUpperCase();
  const conversationStartStatusUnavailable = (
    conversationStartMapped
    && conversationStartProvider?.reason === 'provider_status_unavailable'
  );
  const onboardingAccessReady = !staffDomain && whatsappStatus?.onboarding_access_ready === true;
  const onboardingAccessMapped = !staffDomain && whatsappStatus?.onboarding_access_mapped === true;
  const onboardingAccessProvider = !staffDomain ? (whatsappStatus?.onboarding_access_provider || {}) : {};
  const onboardingAccessStatus = String(onboardingAccessProvider?.status || '').toUpperCase();
  const whatsappAvailable = Boolean(
    !readOnly
    && manualWhatsAppReady
    && supportWindow.open
    && (staffDomain || threadId),
  );

  const configuredWhatsAppTemplateKeys = useMemo(
    () => new Set(whatsappStatus?.templates?.configured_template_keys || []),
    [whatsappStatus],
  );
  const visibleChaseOptions = chaseActionsExpanded ? chaseOptions : [];
  const selectedChaseActions = useMemo(
    () => chaseOptions.filter((action) => selectedChaseKeys.includes(String(action?.key || ''))),
    [chaseOptions, selectedChaseKeys],
  );
  const selectedChaseTemplateCandidates = useMemo(() => {
    if (!staffDomain || selectedChaseActions.length === 0) return [];
    const allTraining = selectedChaseActions.every(
      (action) => String(action?.category || '').toLowerCase() === 'training',
    );
    return allTraining
      ? ['staff_training_reminder', 'training_reminder']
      : ['staff_compliance_reminder', 'compliance_reminder'];
  }, [selectedChaseActions, staffDomain]);
  const selectedChaseTemplateAvailable = Boolean(
    manualWhatsAppReady
    && selectedChaseTemplateCandidates.some((key) => configuredWhatsAppTemplateKeys.has(key)),
  );
  const selectedChaseFingerprint = useMemo(
    () => [...selectedChaseKeys].sort().join('|'),
    [selectedChaseKeys],
  );

  const chaseTemplateAvailable = (action) => {
    if (!manualWhatsAppReady) return false;
    // The provider checks exact semantic and approved fallback templates.
    // Never show Send unless a template is actually configured/approved.
    const candidates = Array.isArray(action?.template_candidates)
      ? action.template_candidates
      : [action?.recommended_template_key];
    return candidates.some((value) => {
      const key = String(value || '').trim();
      return key === 'onboarding_access'
        ? onboardingAccessReady
        : configuredWhatsAppTemplateKeys.has(key);
    });
  };

  const composeDbsMessage = () => buildDBSApplicantMessage({
    firstName: personFirstName,
    organisationReference: dbsOrganisationReference,
    organisationCode: dbsOrganisationCode,
  });

  const prepareDbsMessage = () => {
    if (readOnly || sending) return;
    try {
      const nextBody = composeDbsMessage();
      const nextSubject = 'Please complete your DBS application';
      if (!controlledDraft) {
        setSubject(nextSubject);
        setBody(nextBody);
      }
      onDraftChange?.({
        subject: nextSubject,
        body: nextBody,
        channel: channelValue,
      });
      toast.success('DBS message prepared. Review it before sending.');
    } catch (error) {
      toast.error(error.message || 'Please check the organisation reference');
    }
  };

  const copyDbsMessage = async () => {
    if (readOnly || sending) return;
    try {
      await navigator.clipboard.writeText(composeDbsMessage());
      toast.success('DBS application message copied. Nothing was sent.');
    } catch (error) {
      toast.error(error.message || 'Unable to copy DBS application message');
    }
  };

  const sendEmail = async () => {
    const cleanSubject = subjectValue.trim();
    const cleanBody = bodyValue.trim();
    if (!emailEnabled || readOnly || !cleanSubject || !cleanBody || sending) return;

    setSending(true);
    try {
      const endpoint = staffDomain
        ? `${API}/employees/${employeeId}/staff-communications/send-email`
        : `${API}/employees/${employeeId}/communications/send-manual-email`;
      const response = await axios.post(
        endpoint,
        { subject: cleanSubject, body: cleanBody },
        { headers },
      );
      clearDraft();
      toast.success(staffDomain ? 'Staff email sent' : 'Applicant email sent');
      await notifySent(response.data || null);
    } catch (error) {
      toast.error(safeDetail(error, 'Email could not be sent'));
    } finally {
      setSending(false);
    }
  };

  const prepareManualApplicantWhatsAppHelp = async (reason) => {
    if (manualHelpPrepared?.message) return manualHelpPrepared;

    const response = await axios.post(
      `${API}/recruitment/applicants/${employeeId}/portal-access`,
      { send_email: false, reason },
      { headers },
    );
    const secureLink = response.data?.portal_url || response.data?.portal_login_url;
    if (!secureLink) {
      throw new Error('Secure portal link was not returned');
    }
    const prepared = {
      message: buildManualApplicantWhatsAppHelpMessage(personFirstName, secureLink, nextActionTitle),
      recipient: String(response.data?.whatsapp_recipient || '').trim(),
    };
    setManualHelpPrepared(prepared);
    return prepared;
  };

  const copyManualApplicantWhatsAppHelp = async () => {
    if (staffDomain || readOnly || copyingManualHelp || openingManualHelp) return;

    setCopyingManualHelp(true);
    try {
      const prepared = await prepareManualApplicantWhatsAppHelp(
        'Admin copied a manual WhatsApp support message from applicant messaging',
      );
      await navigator.clipboard.writeText(prepared.message);
      toast.success('WhatsApp help message copied — paste it into WhatsApp');
    } catch (error) {
      toast.error(safeDetail(error, 'Could not copy the WhatsApp help message'));
    } finally {
      setCopyingManualHelp(false);
    }
  };

  const openManualApplicantWhatsAppHelp = async () => {
    if (staffDomain || readOnly || openingManualHelp || copyingManualHelp) return;

    // Open a blank tab synchronously so browsers do not block the handoff after
    // the secure-link request completes. CareOps still never presses Send.
    const whatsappWindow = window.open('about:blank', '_blank');
    if (whatsappWindow) whatsappWindow.opener = null;

    setOpeningManualHelp(true);
    try {
      const prepared = await prepareManualApplicantWhatsAppHelp(
        'Admin opened a manual WhatsApp support message from applicant messaging',
      );
      if (!prepared.recipient) {
        if (whatsappWindow) whatsappWindow.close();
        await navigator.clipboard.writeText(prepared.message);
        toast.success('Applicant phone needs an international WhatsApp number — message copied instead');
        return;
      }
      const whatsappUrl = `https://wa.me/${prepared.recipient}?text=${encodeURIComponent(prepared.message)}`;
      if (whatsappWindow) {
        whatsappWindow.location.href = whatsappUrl;
        toast.success('WhatsApp opened with the secure help message ready to review');
      } else {
        await navigator.clipboard.writeText(prepared.message);
        toast.success('Pop-up blocked — WhatsApp help message copied instead');
      }
    } catch (error) {
      if (whatsappWindow) whatsappWindow.close();
      toast.error(safeDetail(error, 'Could not open the WhatsApp help message'));
    } finally {
      setOpeningManualHelp(false);
    }
  };

  const sendOnboardingAccess = async () => {
    if (staffDomain || readOnly || !onboardingAccessReady || sending) return;

    const nextRequestId = accessRequestId || createMessageRequestId();
    if (!accessRequestId) setAccessRequestId(nextRequestId);
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/employees/${employeeId}/communications/send-whatsapp`,
        { template_key: 'onboarding_access', request_id: nextRequestId },
        { headers },
      );
      setAccessRequestId('');
      setAccessSent(true);
      setLastWhatsAppRecipient(String(response.data?.message?.to_phone || '').trim());
      toast.success(response.data?.idempotent_replay
        ? 'Secure onboarding link already sent'
        : 'Secure onboarding link sent');
      await notifySent(response.data || null);
    } catch (error) {
      toast.error(safeDetail(error, 'Secure onboarding link could not be sent'));
    } finally {
      setSending(false);
    }
  };

  const sendTrainingBookingConfirmation = async () => {
    if (!staffDomain || readOnly || sending || !bookingConfirmed ||
        !whatsappStatus?.training_booking_ready === true ||
        Object.values(trainingBooking).some((value) => !String(value).trim())) return;

    const nextRequestId = trainingBookingRequestId || createMessageRequestId();
    if (!trainingBookingRequestId) setTrainingBookingRequestId(nextRequestId);
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/employees/${employeeId}/staff-communications/send-whatsapp`,
        {
          template_key: 'training_booking_confirmation',
          request_id: nextRequestId,
          training_booking: { ...trainingBooking, booking_confirmed: true },
        },
        { headers },
      );
      toast.success(response.data?.idempotent_replay
        ? 'Training booking confirmation already recorded'
        : 'Training booking confirmation accepted by WhatsApp');
      setTrainingBookingRequestId('');
      setBookingConfirmed(false);
      await notifySent(response.data || null);
    } catch (error) {
      // Preserve the original request ID after ambiguous provider responses.
      toast.error(safeDetail(error, 'Could not confirm WhatsApp delivery. Check message history before retrying.'));
    } finally {
      setSending(false);
    }
  };

  const startWhatsAppConversation = async () => {
    if (readOnly || !conversationStartReady || sending) return;

    const nextRequestId = openerRequestId || createMessageRequestId();
    if (!openerRequestId) setOpenerRequestId(nextRequestId);
    setSending(true);
    try {
      const endpoint = staffDomain
        ? `${API}/employees/${employeeId}/staff-communications/send-whatsapp`
        : `${API}/employees/${employeeId}/communications/send-whatsapp`;
      const response = await axios.post(
        endpoint,
        { template_key: 'conversation_start', request_id: nextRequestId },
        { headers },
      );
      setOpenerRequestId('');
      setOpenerSent(true);
      setLastWhatsAppRecipient(String(response.data?.message?.to_phone || '').trim());
      toast.success(response.data?.idempotent_replay
        ? 'WhatsApp conversation invitation already sent'
        : 'WhatsApp conversation invitation sent');
      await notifySent(response.data || null);
    } catch (error) {
      // Keep the same request id if the provider outcome may be ambiguous.
      toast.error(safeDetail(error, 'WhatsApp conversation could not be started'));
    } finally {
      setSending(false);
    }
  };

  const sendWhatsApp = async () => {
    const cleanBody = bodyValue.trim();
    if (!whatsappAvailable || !cleanBody || sending) return;

    const nextRequestId = requestId || createMessageRequestId();
    if (!requestId) setRequestId(nextRequestId);
    setSending(true);
    try {
      const endpoint = staffDomain
        ? `${API}/employees/${employeeId}/staff-communications/reply-whatsapp`
        : `${API}/portal-inbox/threads/${threadId}/reply-whatsapp`;
      const response = await axios.post(
        endpoint,
        { body: cleanBody, request_id: nextRequestId },
        { headers },
      );
      clearDraft({ keepSubject: true });
      setRequestId('');
      toast.success(response.data?.idempotent_replay ? 'WhatsApp reply already sent' : 'WhatsApp reply sent');
      await notifySent(response.data || null);
    } catch (error) {
      // Keep the same id when a provider outcome may be ambiguous.
      toast.error(safeDetail(error, 'WhatsApp reply could not be sent'));
    } finally {
      setSending(false);
    }
  };

  const prepareChaseAction = async (action) => {
    const endpoint = staffDomain
      ? `${API}/employees/${employeeId}/staff-communications/prepare-chase`
      : `${API}/employees/${employeeId}/communications/prepare-chase`;
    const response = await axios.post(
      endpoint,
      { action_key: action.key },
      { headers },
    );
    return response.data || {};
  };

  const copyChaseMessage = async (action) => {
    if (readOnly || chaseBusyKey) return;
    setChaseBusyKey(`${action.key}:copy`);
    try {
      const prepared = await prepareChaseAction(action);
      if (!prepared.message) throw new Error('CareOps did not return a reminder message');
      await navigator.clipboard.writeText(prepared.message);
      toast.success('Reminder copied with a fresh secure CareOps link');
    } catch (error) {
      toast.error(safeDetail(error, 'Could not prepare this reminder'));
    } finally {
      setChaseBusyKey('');
    }
  };

  const openChaseInWhatsApp = async (action) => {
    if (readOnly || chaseBusyKey) return;
    const whatsappWindow = window.open('about:blank', '_blank');
    if (whatsappWindow) whatsappWindow.opener = null;
    setChaseBusyKey(`${action.key}:open`);
    try {
      const prepared = await prepareChaseAction(action);
      if (!prepared.message) throw new Error('CareOps did not return a reminder message');
      const recipient = String(prepared.whatsapp_recipient || '').trim();
      if (!recipient) {
        if (whatsappWindow) whatsappWindow.close();
        await navigator.clipboard.writeText(prepared.message);
        toast.success('No WhatsApp-ready number — reminder copied instead');
        return;
      }
      const whatsappUrl = `https://wa.me/${recipient}?text=${encodeURIComponent(prepared.message)}`;
      if (whatsappWindow) {
        whatsappWindow.location.href = whatsappUrl;
        toast.success('WhatsApp opened with the reminder ready to review');
      } else {
        await navigator.clipboard.writeText(prepared.message);
        toast.success('Pop-up blocked — reminder copied instead');
      }
    } catch (error) {
      if (whatsappWindow) whatsappWindow.close();
      toast.error(safeDetail(error, 'Could not open this reminder in WhatsApp'));
    } finally {
      setChaseBusyKey('');
    }
  };

  const loadChaseIntoComposer = async (action) => {
    if (readOnly || chaseBusyKey) return;
    setChaseBusyKey(`${action.key}:compose`);
    try {
      const prepared = await prepareChaseAction(action);
      if (!prepared.message) throw new Error('CareOps did not return a reminder message');
      updateDraftField('body', prepared.message);
      toast.success('Reminder prepared — review it and press Send WhatsApp');
    } catch (error) {
      toast.error(safeDetail(error, 'Could not prepare this reminder'));
    } finally {
      setChaseBusyKey('');
    }
  };

  const sendChaseTemplate = async (action) => {
    if (readOnly || sending || chaseBusyKey || !chaseTemplateAvailable(action)) return;
    const existingRequestId = chaseRequestIds[action.key] || '';
    const nextRequestId = existingRequestId || createMessageRequestId();
    if (!existingRequestId) {
      setChaseRequestIds((current) => ({ ...current, [action.key]: nextRequestId }));
    }
    setChaseBusyKey(`${action.key}:send`);
    setSending(true);
    try {
      const endpoint = staffDomain
        ? `${API}/employees/${employeeId}/staff-communications/send-whatsapp`
        : `${API}/employees/${employeeId}/communications/send-whatsapp`;
      const response = await axios.post(
        endpoint,
        {
          action_key: action.key,
          request_id: nextRequestId,
        },
        { headers },
      );
      setChaseRequestIds((current) => {
        const next = { ...current };
        delete next[action.key];
        return next;
      });
      setLastWhatsAppRecipient(String(response.data?.message?.to_phone || '').trim());
      toast.success(response.data?.idempotent_replay ? 'WhatsApp reminder already sent' : 'WhatsApp reminder sent');
      await notifySent(response.data || null);
    } catch (error) {
      // Retain the same request id after an ambiguous provider outcome.
      toast.error(safeDetail(error, 'WhatsApp reminder could not be sent'));
    } finally {
      setSending(false);
      setChaseBusyKey('');
    }
  };

  const toggleSelectedChaseAction = (actionKey) => {
    const key = String(actionKey || '').trim();
    if (!key || sending || chaseBusyKey) return;
    setSelectedChaseKeys((current) => (
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key]
    ));
  };

  const selectAllChaseActions = () => {
    if (sending || chaseBusyKey) return;
    setSelectedChaseKeys(
      chaseOptions
        .map((action) => String(action?.key || '').trim())
        .filter(Boolean),
    );
  };

  const clearSelectedChaseActions = () => {
    if (sending || chaseBusyKey) return;
    setSelectedChaseKeys([]);
  };

  const prepareSelectedStaffChase = async () => {
    if (selectedChaseKeys.length === 0) {
      throw new Error('Select at least one CareOps action');
    }
    const endpoint = staffDomain
      ? `${API}/employees/${employeeId}/staff-communications/prepare-chase`
      : `${API}/employees/${employeeId}/communications/prepare-chase-batch`;
    const response = await axios.post(
      endpoint,
      { action_keys: selectedChaseKeys },
      { headers },
    );
    return response.data || {};
  };

  const sendSelectedStaffChase = async () => {
    if (
      readOnly
      || sending
      || chaseBusyKey
      || selectedChaseKeys.length === 0
      || (supportWindow.open ? !whatsappAvailable : (!staffDomain || !selectedChaseTemplateAvailable))
    ) return;

    const requestKey = `selected:${selectedChaseFingerprint}`;
    const existingRequestId = chaseRequestIds[requestKey] || '';
    const nextRequestId = existingRequestId || createMessageRequestId();
    if (!existingRequestId) {
      setChaseRequestIds((current) => ({ ...current, [requestKey]: nextRequestId }));
    }

    const selectedCount = selectedChaseKeys.length;
    setChaseBusyKey('selected:send');
    setSending(true);
    try {
      let response;
      if (supportWindow.open) {
        const prepared = await prepareSelectedStaffChase();
        if (!prepared.message) throw new Error('CareOps did not return a combined reminder message');
        response = await axios.post(
          staffDomain
            ? `${API}/employees/${employeeId}/staff-communications/reply-whatsapp`
            : `${API}/portal-inbox/threads/${threadId}/reply-whatsapp`,
          {
            body: prepared.message,
            request_id: nextRequestId,
          },
          { headers },
        );
      } else {
        response = await axios.post(
          `${API}/employees/${employeeId}/staff-communications/send-whatsapp`,
          {
            action_keys: selectedChaseKeys,
            request_id: nextRequestId,
          },
          { headers },
        );
      }

      setChaseRequestIds((current) => {
        const next = { ...current };
        delete next[requestKey];
        return next;
      });
      setSelectedChaseKeys([]);
      setLastWhatsAppRecipient(String(response.data?.message?.to_phone || '').trim());
      toast.success(
        response.data?.idempotent_replay
          ? 'Combined WhatsApp reminder already sent'
          : `One WhatsApp reminder sent for ${selectedCount} selected action${selectedCount === 1 ? '' : 's'}`,
      );
      await notifySent(response.data || null);
    } catch (error) {
      // Preserve the same request id after an ambiguous provider outcome.
      toast.error(safeDetail(error, 'Combined WhatsApp reminder could not be sent'));
    } finally {
      setSending(false);
      setChaseBusyKey('');
    }
  };

  const prepareSelectedEmailTasks = async () => {
    if (readOnly || chaseBusyKey || selectedChaseKeys.length === 0) return;
    setChaseBusyKey('email:prepare');
    try {
      const prepared = await prepareSelectedStaffChase();
      if (!prepared.message) throw new Error('No secure task message was prepared');
      const draftSubject = selectedChaseKeys.length === 1
        ? 'CareOps: please complete your outstanding action'
        : 'CareOps: your outstanding actions';
      if (!controlledDraft) {
        setSubject(draftSubject);
        setBody(prepared.message);
      }
      onDraftChange?.({
        subject: draftSubject, body: prepared.message, channel: 'email',
      });
      toast.success('Task email prepared for your review. Nothing was sent.');
    } catch (error) {
      toast.error(safeDetail(error, 'Could not prepare task email'));
    } finally {
      setChaseBusyKey('');
    }
  };

  const copySelectedStaffChase = async () => {
    if (readOnly || chaseBusyKey || selectedChaseKeys.length === 0) return;
    setChaseBusyKey('selected:copy');
    try {
      const prepared = await prepareSelectedStaffChase();
      if (!prepared.message) throw new Error('CareOps did not return a combined reminder message');
      await navigator.clipboard.writeText(prepared.message);
      toast.success('Combined reminder copied with one fresh secure CareOps link');
    } catch (error) {
      toast.error(safeDetail(error, 'Could not prepare the combined reminder'));
    } finally {
      setChaseBusyKey('');
    }
  };

  const openSelectedStaffChaseInWhatsApp = async () => {
    if (readOnly || chaseBusyKey || selectedChaseKeys.length === 0) return;
    const whatsappWindow = window.open('about:blank', '_blank');
    if (whatsappWindow) whatsappWindow.opener = null;
    setChaseBusyKey('selected:open');
    try {
      const prepared = await prepareSelectedStaffChase();
      if (!prepared.message) throw new Error('CareOps did not return a combined reminder message');
      const recipient = String(prepared.whatsapp_recipient || '').trim();
      if (!recipient) {
        if (whatsappWindow) whatsappWindow.close();
        await navigator.clipboard.writeText(prepared.message);
        toast.success('No WhatsApp-ready number — combined reminder copied instead');
        return;
      }
      const whatsappUrl = `https://wa.me/${recipient}?text=${encodeURIComponent(prepared.message)}`;
      if (whatsappWindow) {
        whatsappWindow.location.href = whatsappUrl;
        toast.success('WhatsApp opened with one combined reminder ready to review');
      } else {
        await navigator.clipboard.writeText(prepared.message);
        toast.success('Pop-up blocked — combined reminder copied instead');
      }
    } catch (error) {
      if (whatsappWindow) whatsappWindow.close();
      toast.error(safeDetail(error, 'Could not open the combined reminder in WhatsApp'));
    } finally {
      setChaseBusyKey('');
    }
  };

  return (
    <div className="space-y-3" data-testid={`person-message-composer-${domain}`}>
      <div className="flex flex-wrap items-center justify-between gap-1.5">
        <p className="text-sm font-semibold text-slate-900">Message</p>
        <p className="text-xs leading-5 text-slate-600">Nothing sends automatically.</p>
      </div>

      {staffDomain && !readOnly && (
        <StaffDirectLinks employeeId={employeeId} token={token} readOnly={readOnly} />
      )}

      {!readOnly ? (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50" data-testid="carecheck-dbs-message">
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
            onClick={() => setDbsToolsOpen((value) => !value)}
            aria-expanded={dbsToolsOpen}
            aria-label="DBS application link"
          >
            <span className="text-sm font-semibold text-slate-900">DBS application link</span>
            <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${dbsToolsOpen ? 'rotate-180' : ''}`} />
          </button>
          {dbsToolsOpen ? (
            <div className="space-y-3 border-t border-slate-200 p-3">
              <p className="text-xs text-slate-700">
                Prepare the public Care Check start link for this person. Use this workspace's own reference.
                Nothing is sent automatically.
              </p>
              <div>
                <Label htmlFor={`carecheck-org-reference-${domain}`}>Organisation reference</Label>
                <Input
                  id={`carecheck-org-reference-${domain}`}
                  value={dbsOrganisationReference}
                  onChange={(event) => setDbsOrganisationReference(event.target.value)}
                  placeholder="Enter Care Check organisation reference"
                  maxLength={100}
                  disabled={sending}
                />
              </div>
              <div>
                <Label htmlFor={`carecheck-org-code-${domain}`}>Organisation code (only if supplied)</Label>
                <Input
                  id={`carecheck-org-code-${domain}`}
                  value={dbsOrganisationCode}
                  onChange={(event) => setDbsOrganisationCode(event.target.value)}
                  maxLength={100}
                  disabled={sending}
                />
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={copyDbsMessage}
                  disabled={sending || !dbsOrganisationReference.trim()}
                >
                  <Copy className="mr-1.5 h-4 w-4" />
                  Copy DBS message
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={prepareDbsMessage}
                  disabled={sending || !dbsOrganisationReference.trim()}
                >
                  Prepare DBS message
                </Button>
              </div>
              {channelValue === 'whatsapp' && !whatsappAvailable ? (
                <p className="text-xs text-amber-800">
                  A new WhatsApp message cannot be sent through CareDock outside the active reply window
                  without an approved Meta template. Copy the message for manual follow-up or use email.
                </p>
              ) : null}
              <p className="text-xs text-slate-600">
                Applicants complete their own details and any payment on Care Check.
                Submission does not replace the employer's DBS eligibility and ID verification.
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Message channel">
        <Button
          type="button"
          size="sm"
          variant={channelValue === 'email' ? 'default' : 'outline'}
          onClick={() => updateDraftField('channel', 'email')}
        >
          <Mail className="mr-1.5 h-4 w-4" />
          Email
        </Button>
        <Button
          type="button"
          size="sm"
          variant={channelValue === 'whatsapp' ? 'default' : 'outline'}
          onClick={() => updateDraftField('channel', 'whatsapp')}
        >
          <MessageCircle className="mr-1.5 h-4 w-4" />
          WhatsApp
        </Button>
      </div>

      {channelValue === 'email' ? (
        emailEnabled && !readOnly ? (
          <div className="space-y-3">
            <div>
              <Label htmlFor={`manual-message-subject-${domain}`}>Subject</Label>
              <Input
                id={`manual-message-subject-${domain}`}
                className="mt-1.5"
                value={subjectValue}
                onChange={(event) => updateDraftField('subject', event.target.value)}
                maxLength={200}
                placeholder="Message subject"
                disabled={sending}
              />
            </div>
            <div className="rounded-lg border border-slate-200 p-3" data-testid="email-task-links">
              <button type="button" className="text-sm font-semibold text-slate-900 underline"
                aria-expanded={emailTasksExpanded}
                onClick={() => setEmailTasksExpanded((v) => !v)}>
                Choose outstanding task links
              </button>
              {emailTasksExpanded && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-slate-600">
                    Select what this person actually needs to complete. This prepares an email only.
                  </p>
                  {chaseOptionsLoading ? <p className="text-xs">Loading current tasks…</p> : null}
                  {!chaseOptionsLoading && chaseOptions.length === 0 ? (
                    <p className="text-xs text-slate-600">No outstanding actions to request right now.</p>
                  ) : (
                    chaseOptions.map((action) => (
                      <label key={action.key} className="flex items-start gap-2 text-sm text-slate-800">
                        <input type="checkbox" className="mt-1" checked={selectedChaseKeys.includes(action.key)}
                          disabled={sending || Boolean(chaseBusyKey)}
                          onChange={() => toggleSelectedChaseAction(action.key)} />
                        <span>{action.title}</span>
                      </label>
                    ))
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={prepareSelectedEmailTasks}
                    disabled={sending || Boolean(chaseBusyKey) || selectedChaseKeys.length === 0}>
                    Prepare selected tasks email
                  </Button>
                  <p className="text-xs text-slate-500">Nothing sends until you review and press Send email.</p>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor={`manual-message-body-${domain}`}>Message</Label>
              <Textarea
                id={`manual-message-body-${domain}`}
                className="mt-1.5 min-h-[160px]"
                rows={7}
                maxLength={10000}
                value={bodyValue}
                onChange={(event) => updateDraftField('body', event.target.value)}
                placeholder="Type or paste your message…"
                disabled={sending}
              />
            </div>
            <div className="flex justify-end">
              <Button
                type="button"
                onClick={sendEmail}
                disabled={sending || !subjectValue.trim() || !bodyValue.trim()}
              >
                {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Send email
              </Button>
            </div>
          </div>
        ) : (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            {emailReason || 'Email is not available for this record.'}
          </div>
        )
      ) : (
        <div className="space-y-3">
          {latestFailure ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-900" data-testid="latest-whatsapp-failure">
              <p className="font-semibold">{latestFailure.title}</p>
              {latestFailure.recipient ? (
                <p className="mt-1">Attempted destination: <strong>+{latestFailure.recipient}</strong></p>
              ) : null}
              <p className="mt-1">{latestFailure.code ? `Meta error ${latestFailure.code}: ` : ''}{latestFailure.reason}</p>
              <p className="mt-1">
                {latestFailure.accountLevel
                  ? 'This is an Osabea WhatsApp Business Account setting, not an applicant-number failure. Fix Meta billing first; normal WhatsApp remains available below as a manual fallback.'
                  : 'Verify that this is the applicant\'s correct WhatsApp number before retrying. You can also use normal WhatsApp below to open the same saved number manually.'}
              </p>
            </div>
          ) : null}

          {(chaseOptionsLoading || chaseOptions.length > 0) && !readOnly ? (
            <div className="overflow-hidden rounded-lg border border-blue-200 bg-blue-50" data-testid="careops-chase-actions">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
                onClick={() => setChaseActionsExpanded((value) => !value)}
                aria-expanded={chaseActionsExpanded}
                aria-label="CareOps actions"
              >
                <span>
                  <span className="text-sm font-semibold text-blue-950">Task links</span>
                  <span className="ml-2 text-xs text-blue-800">
                    {chaseOptionsLoading ? 'Loading…' : `${chaseOptions.length} outstanding`}
                  </span>
                </span>
                {chaseOptionsLoading
                  ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blue-700" />
                  : <ChevronDown className={`h-4 w-4 shrink-0 text-blue-700 transition-transform ${chaseActionsExpanded ? 'rotate-180' : ''}`} />}
              </button>
              {chaseActionsExpanded && chaseOptions.length > 0 && !chaseOptionsLoading ? (
                <div className="flex items-center justify-end gap-1 border-t border-blue-200 px-2 py-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={selectAllChaseActions}
                    disabled={sending || Boolean(chaseBusyKey)}
                  >
                    Select all
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={clearSelectedChaseActions}
                    disabled={sending || Boolean(chaseBusyKey) || selectedChaseKeys.length === 0}
                  >
                    Clear
                  </Button>
                </div>
              ) : null}
              {visibleChaseOptions.length > 0 ? (
                <div className="space-y-3 border-t border-blue-200 p-3 sm:p-4">
                  {visibleChaseOptions.map((action) => {
                    const busy = chaseBusyKey.startsWith(`${action.key}:`);
                    const approvedTemplateAvailable = chaseTemplateAvailable(action);
                    const selected = selectedChaseKeys.includes(String(action.key || ''));
                    return (
                      <div
                        key={action.key}
                        className={`rounded-lg border bg-white p-3 sm:p-4 ${selected ? 'border-primary/50 ring-1 ring-primary/20' : 'border-blue-200'}`}
                        data-testid={`chase-action-${action.key}`}
                      >
                        <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-3">
                          <Checkbox
                            checked={selected}
                            onCheckedChange={() => toggleSelectedChaseAction(action.key)}
                            disabled={sending || Boolean(chaseBusyKey)}
                            aria-label={`Select ${action.title}`}
                            className="mt-0.5"
                          />
                          <div className="min-w-0 break-words" data-testid="chase-action-details">
                            <p className="text-sm font-semibold leading-5 text-slate-950">{action.title}</p>
                            {action.description ? <p className="mt-1 text-sm leading-5 text-slate-700">{action.description}</p> : null}
                            <p className="mt-1.5 text-xs leading-5 text-blue-800">{staffDomain ? 'The employee' : 'The applicant'} can follow this secure link directly to the task.</p>
                          </div>
                          {!staffDomain ? (
                          <div className="col-span-2 grid min-w-0 w-full grid-cols-1 gap-2 border-t border-slate-100 pt-3 sm:col-start-2 sm:col-span-1 sm:flex sm:flex-wrap sm:items-center sm:justify-start" data-testid="chase-action-controls">
                            {supportWindow.open ? (
                              <Button type="button" size="sm" className="h-auto min-h-9 w-full whitespace-normal py-2 sm:w-auto" onClick={() => loadChaseIntoComposer(action)} disabled={busy || sending}>
                                Write reminder
                              </Button>
                            ) : approvedTemplateAvailable ? (
                              <Button type="button" size="sm" className="h-auto min-h-9 w-full whitespace-normal py-2 sm:w-auto" onClick={() => sendChaseTemplate(action)}
                                disabled={busy || sending} data-testid={`send-chase-whatsapp-${action.key}`}>
                                Send WhatsApp reminder
                              </Button>
                            ) : null}
                            <Button type="button" size="sm" variant="outline" className="h-auto min-h-9 w-full whitespace-normal py-2 sm:w-auto" onClick={() => copyChaseMessage(action)} disabled={busy || sending}>
                              <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy link message
                            </Button>
                            <Button type="button" size="sm" variant="outline" className="h-auto min-h-9 w-full whitespace-normal py-2 sm:w-auto" onClick={() => openChaseInWhatsApp(action)} disabled={busy || sending}>
                              <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Open WhatsApp
                            </Button>
                          </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                  {(
                    <div className="rounded-md border border-blue-200 bg-white p-3" data-testid="selected-chase-actions">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs font-semibold text-slate-800">
                          {selectedChaseKeys.length} of {chaseOptions.length} selected
                        </p>
                        <p className="text-[11px] text-slate-500">
                          Tick what {staffDomain ? 'this employee' : 'the applicant'} needs to do. One message, one secure link.
                        </p>
                      </div>
                      <p className="mt-2 text-xs text-blue-900">
                        Prepare a private task link for manual WhatsApp or email. Copying never sends a message.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={copySelectedStaffChase}
                          disabled={sending || Boolean(chaseBusyKey) || selectedChaseKeys.length === 0}
                        >
                          <Copy className="mr-1.5 h-3.5 w-3.5" />
                          Copy task link message
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={openSelectedStaffChaseInWhatsApp}
                          disabled={sending || Boolean(chaseBusyKey) || selectedChaseKeys.length === 0}
                        >
                          <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                          Open combined WhatsApp
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={sendSelectedStaffChase}
                          disabled={
                            sending
                            || Boolean(chaseBusyKey)
                            || selectedChaseKeys.length === 0
                            || (supportWindow.open ? !whatsappAvailable : (!staffDomain || !selectedChaseTemplateAvailable))
                          }
                          data-testid="send-selected-chase-whatsapp"
                        >
                          {chaseBusyKey === 'selected:send' || sending
                            ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                            : <Send className="mr-1.5 h-3.5 w-3.5" />}
                          Send one WhatsApp reminder
                        </Button>
                      </div>
                      {selectedChaseKeys.length === 0 ? (
                        <p className="mt-2 text-[11px] text-blue-800">Tick one or more outstanding items above.</p>
                      ) : supportWindow.open && whatsappAvailable ? (
                        <p className="mt-2 text-[11px] text-emerald-800">
                          CareOps will create one exact combined reminder listing the selected items and send it in the open WhatsApp reply window.
                        </p>
                      ) : staffDomain && selectedChaseTemplateAvailable ? (
                        <p className="mt-2 text-[11px] text-emerald-800">
                          CareOps will send one approved reminder template and record all selected actions against that message. The secure link opens the worker's current CareOps actions.
                        </p>
                      ) : (
                        <p className="mt-2 text-[11px] text-amber-800">
                          An approved combined reminder template is not currently available. Use Copy task link message or Open WhatsApp to share one combined message manually.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}

          {!readOnly && staffDomain ? (
            <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3" data-testid="training-booking-confirmation">
              <div>
                <p className="text-sm font-semibold text-slate-950">Training booking confirmation</p>
                <p className="text-xs text-slate-600 mt-1">
                  Send confirmed course details using an approved WhatsApp template—even if the worker has not messaged first.
                  Training remains Booked until attendance and the certificate are verified.
                </p>
              </div>
              {whatsappStatus?.training_booking_ready === true ? (
                <>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {[
                      ['course_title', 'Course name', 'CSTF (All in One Day) Mandatory Training'],
                      ['training_date', 'Course date', 'Friday 30 October 2026'],
                      ['arrival_time', 'Arrival time', '09:30'],
                      ['training_time', 'Training time', '10:00–17:30'],
                      ['venue_address', 'Venue and address', 'Orida Hotel, Bearsted Road, Maidstone, ME14 5AA'],
                    ].map(([key, label, placeholder]) => (
                      <div key={key} className={key === 'venue_address' ? 'sm:col-span-2' : ''}>
                        <Label htmlFor={`training-booking-${key}`}>{label}</Label>
                        <Input
                          id={`training-booking-${key}`}
                          value={trainingBooking[key]}
                          placeholder={placeholder}
                          disabled={sending}
                          maxLength={key === 'venue_address' ? 220 : 150}
                          onChange={(event) => {
                            setTrainingBooking((previous) => ({ ...previous, [key]: event.target.value }));
                            setBookingConfirmed(false);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="training-booking-confirmed"
                      checked={bookingConfirmed}
                      onCheckedChange={(checked) => setBookingConfirmed(checked === true)}
                      disabled={sending}
                    />
                    <Label htmlFor="training-booking-confirmed" className="text-xs leading-relaxed">
                      I have checked this worker’s individual booking confirmation from the training provider.
                    </Label>
                  </div>
                  <div className="flex justify-end">
                    <Button type="button" disabled={sending || !bookingConfirmed ||
                      Object.values(trainingBooking).some((value) => !String(value).trim())}
                      onClick={sendTrainingBookingConfirmation}
                      data-testid="send-training-booking-whatsapp">
                      {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                      Send confirmed booking
                    </Button>
                  </div>
                </>
              ) : (
                <p role="status" className="text-xs text-amber-900 rounded-md bg-amber-50 px-3 py-2">
                  Waiting for the training booking template to be approved by Meta and configured for Osabea.
                  You can still email the individual course confirmation now.
                </p>
              )}
            </div>
          ) : null}

          {!readOnly && (staffDomain || threadId) ? (
            <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="whatsapp-composer">
              <Label htmlFor={`manual-whatsapp-body-${domain}`}>Message</Label>
              <Textarea
                id={`manual-whatsapp-body-${domain}`}
                className="mt-1.5 min-h-[72px]"
                rows={3}
                maxLength={4096}
                value={bodyValue}
                onChange={(event) => updateDraftField('body', event.target.value)}
                placeholder="Type or paste your WhatsApp message…"
                disabled={sending}
              />
              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-slate-500">
                  {whatsappAvailable
                    ? 'Write your reply here. CareOps sends exactly what you enter.'
                    : supportWindow.open
                      ? 'WhatsApp sending is temporarily unavailable for this workspace.'
                      : 'Write now; Send unlocks after the applicant replies here.'}
                </p>
                <Button
                  type="button"
                  className="shrink-0"
                  onClick={sendWhatsApp}
                  disabled={sending || !bodyValue.trim() || !whatsappAvailable}
                >
                  {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MessageCircle className="mr-2 h-4 w-4" />}
                  Send WhatsApp
                </Button>
              </div>
            </div>
          ) : null}

          <div className={`rounded-md border px-3 py-2 text-xs ${
            whatsappAvailable || openerAlreadySent || accessSent
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}>
            <p className="font-semibold">Human WhatsApp message</p>
            {!manualWhatsAppReady ? (
              <p className="mt-1">The shared WhatsApp connection is not ready for this workspace.</p>
            ) : supportWindow.open ? (
              <p className="mt-1">
                This person has messaged Osabea recently, so you can send a personal reply.
                {supportWindow.expiresAt ? ` The reply window is open until ${formatDateTime(supportWindow.expiresAt)}.` : ''}
              </p>
            ) : accessSent ? (
              <p className="mt-1">
                A fresh secure CareOps onboarding link was sent{knownWhatsAppRecipient ? ` to +${knownWhatsAppRecipient}` : ''}. They can tap it to continue onboarding and reply here if they need help.
              </p>
            ) : !staffDomain && onboardingAccessReady ? (
              <p className="mt-1">
                You can send a fresh secure CareOps access link now. The applicant can continue onboarding without finding an old email or remembering a password.
              </p>
            ) : openerAlreadySent ? (
              <div className="mt-1 space-y-1">
                <p>
                  Conversation invitation sent{knownWhatsAppRecipient ? ` to +${knownWhatsAppRecipient}` : ''}.
                </p>
                <p>
                  You can prepare your reply now. Send becomes available automatically after the applicant replies, and their reply appears in this Recruitment conversation.
                </p>
                <p>
                  If they still have not replied, you can explicitly send another approved conversation invitation below.
                </p>
              </div>
            ) : conversationStartReady ? (
              <div className="mt-1 space-y-1">
                <p>
                  They have not opened a reply window yet. CareOps will send the approved invitation to the WhatsApp number saved on this applicant's CareOps profile.
                </p>
                <p>
                  After they reply, this same WhatsApp tab becomes a normal message box for your personal replies.
                </p>
              </div>
            ) : !staffDomain && onboardingAccessStatus === 'PENDING' ? (
              <p className="mt-1">
                The secure onboarding-link WhatsApp message is waiting for Meta approval. The generic first-contact message may still be available below.
              </p>
            ) : conversationStartStatus === 'PENDING' ? (
              <p className="mt-1">
                Osabea&apos;s first-contact WhatsApp message is waiting for Meta approval. You can use email now; the WhatsApp start button will appear automatically once Meta approves it.
              </p>
            ) : ['REJECTED', 'PAUSED', 'DISABLED'].includes(conversationStartStatus) ? (
              <p className="mt-1">
                Meta has not approved Osabea&apos;s first-contact WhatsApp message for use. It needs review in WhatsApp Manager before CareOps can start a new WhatsApp conversation.
              </p>
            ) : conversationStartStatusUnavailable ? (
              <p className="mt-1">
                CareOps could not verify the first-contact WhatsApp approval with Meta, so starting a new WhatsApp conversation is temporarily disabled. Email is still available.
              </p>
            ) : conversationStartMapped || onboardingAccessMapped ? (
              <p className="mt-1">
                A first-contact WhatsApp message is configured but is not approved for sending yet. Email is still available.
              </p>
            ) : (
              <p className="mt-1">
                First-contact WhatsApp is not configured yet. Email is still available.
              </p>
            )}
          </div>

          {!supportWindow.open && !readOnly && (
            conversationStartReady || (!staffDomain && onboardingAccessReady)
          ) ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="whatsapp-start-actions">
              <p className="text-sm font-semibold text-emerald-950">WhatsApp actions</p>
              <p className="mt-1 text-xs text-emerald-900">
                Use an approved Meta template to contact this applicant now. Free-form replies unlock after they reply.
              </p>
              <div className="mt-3 flex flex-wrap justify-end gap-2">
                {!staffDomain && onboardingAccessReady ? (
                  <Button
                    type="button"
                    onClick={sendOnboardingAccess}
                    disabled={sending}
                    data-testid="send-whatsapp-onboarding-access"
                  >
                    {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                    {accessSent ? 'Send another secure link' : 'Send applicant invite / secure link'}
                  </Button>
                ) : null}
                {conversationStartReady ? (
                  <Button
                    type="button"
                    variant={!staffDomain && onboardingAccessReady ? 'outline' : 'default'}
                    onClick={startWhatsAppConversation}
                    disabled={sending}
                    data-testid="start-whatsapp-conversation"
                  >
                    {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MessageCircle className="mr-2 h-4 w-4" />}
                    {openerAlreadySent ? 'Send another conversation invite' : 'Start WhatsApp conversation'}
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}

          {!supportWindow.open && !readOnly && !staffDomain ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50" data-testid="optional-whatsapp-actions">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
                onClick={() => setWhatsAppActionsExpanded((value) => !value)}
                aria-expanded={whatsappActionsExpanded}
                aria-controls={`optional-whatsapp-actions-${domain}`}
              >
                <span>
                  <span className="block text-sm font-semibold text-slate-900">More WhatsApp actions</span>
                  <span className="block text-xs text-slate-500">Open normal WhatsApp or copy a secure help message</span>
                </span>
                <ChevronDown className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${whatsappActionsExpanded ? 'rotate-180' : ''}`} />
              </button>

              {whatsappActionsExpanded ? (
                <div id={`optional-whatsapp-actions-${domain}`} className="space-y-3 border-t border-slate-200 p-3">
                  {!staffDomain ? (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="manual-whatsapp-help-card">
                      <p className="text-sm font-medium text-blue-950">Use normal WhatsApp now</p>
                      <p className="mt-1 text-xs text-blue-900">
                        Open normal WhatsApp{knownWhatsAppRecipient ? ` to +${knownWhatsAppRecipient}` : ''} with a fresh secure CareOps link already filled in, or copy the message. Nothing is sent automatically.
                      </p>
                      <div className="mt-3 flex flex-wrap justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={copyManualApplicantWhatsAppHelp}
                          disabled={copyingManualHelp || openingManualHelp || sending}
                          data-testid="copy-manual-whatsapp-help"
                        >
                          {copyingManualHelp ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Copy className="mr-2 h-4 w-4" />
                          )}
                          Copy message
                        </Button>
                        <Button
                          type="button"
                          onClick={openManualApplicantWhatsAppHelp}
                          disabled={openingManualHelp || copyingManualHelp || sending}
                          data-testid="open-manual-whatsapp-help"
                        >
                          {openingManualHelp ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <ExternalLink className="mr-2 h-4 w-4" />
                          )}
                          Open WhatsApp
                        </Button>
                      </div>
                    </div>
                  ) : null}

                </div>
              ) : null}
            </div>
          ) : null}

        </div>
      )}
    </div>
  );
}
